/**
 * One plain-language sentence for what Penelope is doing on a game — the basic
 * tier's whole readout. Pure: the ticket and the strip hand it the game row's
 * facts and print what comes back. Regimes are the relay's
 * (relay/src/services/penelope/decide.ts); the words are the trader's.
 */
import type { PenelopeRegime } from '@/hooks/usePenelope';

export interface PenelopeStatusInput {
  /** cfg.enabled */
  armed: boolean;
  /** Armed but not running, and why (row.parked). */
  parked: string | null;
  /** Shadow mode: nothing reaches Kalshi. */
  practice: boolean;
  teamA: string;
  teamB: string;
  /** The baseline runner's snapshot, or null before the first tick. */
  live: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    rests: { A: StatusRest | null; B: StatusRest | null };
    limitRejects?: number;
    lastError?: string | null;
    /** Relay ≥ 2026-10-14: why each side is (not) posting, the follow reference and its age, the last line's age, and the
     *  unpaired tail she is selling back. Older relays send none and the sentence reads as before. */
    desiredWhy?: { A: string | null; B: string | null };
    ref?: { A: { px: number; ageMs: number } | null; B: { px: number; ageMs: number } | null };
    lastFairAgeMs?: number | null;
    tail?: { team: 'A' | 'B'; contracts: number; avgCents: number } | null;
    /** Why the fair is unusable ('stale' = Pinnacle's own timestamp is older than max Pinnacle age) and that age. */
    fairBlock?: string | null;
    vendorAgeMs?: number | null;
    /** Relay ≥ 2026-10-15: Pinnacle's price is past max Pinnacle age but still used (in the feed, game moving). */
    fairStill?: { ageMs: number; driftCents: number | null } | null;
    /** Relay ≥ 2026-10-20 (PR-063): the aux slot's orders (split unit, mini orphan completion) and what each side's last
     *  decision was (keep = the order stays and `desiredWhy` says why it is retained), whether buying a team reduces what
     *  she holds, and what pacing / the venue held back on the last pass. */
    aux?: { A: StatusRest | null; B: StatusRest | null };
    want?: { A: StatusWant; B: StatusWant };
    auxWant?: { A: StatusWant | null; B: StatusWant | null };
    auxWhy?: { A: string | null; B: string | null };
    reducing?: { A: boolean; B: boolean };
    heldBack?: string | null;
    venueHold?: { A: string | null; B: string | null };
  } | null;
}

/** A working order as the sentence reads it: price, what is left (`size`), what it went in at (`placed`), why it was placed. */
interface StatusRest { px: number; size: number; placed?: number; why?: string }
interface StatusWant { kind: 'quote' | 'keep' | 'none'; px: number | null }
type Side = 'A' | 'B';

const secs = (ms: number): string => `${Math.round(ms / 1000)} s`;

/** Why a side is NOT posting, from decide's reason, in the trader's words. Null for reasons that are not worth a line
 *  (nothing to say while she is quoting, or the side is simply not armed / capped). */
function notPostingText(why: string | null | undefined): string | null {
  if (!why) return null;
  let m: RegExpMatchArray | null;
  m = why.match(/^follow: touch (\d+) under the reference (\d+)/);
  if (m) return `best bid ${m[1]}¢ is under the reference ${m[2]}¢`;
  m = why.match(/^follow: touch (\d+) more than (\d+)¢ above the reference (\d+)/);
  if (m) return `best bid ${m[1]}¢ is more than ${m[2]}¢ above the reference ${m[3]}¢`;
  if (/^follow: no reference level yet/.test(why)) return 'no reference yet (no good-value price before Pinnacle went off)';
  m = why.match(/^follow: reference (\d+) is (\d+) s old/);
  if (m) return `reference ${m[1]}¢ is ${m[2]} s old`;
  if (/sell-back waits for Pinnacle/.test(why)) return 'sell-back waits for Pinnacle';
  // followMaxPerRefContracts (Doom 10-18 F1): what she bought and has working against the reference, or why none is known.
  m = why.match(/^follow: allowance used \((.+)\)$/);
  if (m) return `follow allowance used (${m[1]})`;
  if (/no completion in overtime/.test(why)) return 'no completion in overtime';
  m = why.match(/^pair: the (?:lock|completion) (?:is|does) not (?:at|lock at) the touch \((\d+) under (\d+)\)/);
  if (m) return `the lock is ${m[1]}¢, under the best bid ${m[2]}¢`;
  m = why.match(/^edge ([-\d.]+)¢ at the touch (\d+) < ([-\d.]+)/);
  if (m) return `edge ${m[1]}¢ at ${m[2]}¢ (needs ${m[3]})`;
  if (/^(net cap|gross cap)/.test(why)) return `at the ${why}`;
  m = why.match(/^puppy: (.+)$/);
  if (m) return `Puppy Love — ${m[1]}`;
  if (/^outside the entry band/.test(why)) return why;
  m = why.match(/^value: fair ([\d.]+) at or below the best bid (\d+)/);
  if (m) return `Pinnacle fair ${m[1]}¢ is at or below the best bid ${m[2]}¢ (no new entry under the old placement)`;
  m = why.match(/^value: edge at (\d+) seen for (\d+) s of ([\d.]+) s/);
  if (m) return `edge at ${m[1]}¢ has shown for ${m[2]} s of the ${m[3]} s it must hold`;
  if (/^no price with edge/.test(why)) return 'no price with edge';
  m = why.match(/^edge only (\d+)¢ behind the touch/);
  if (m) return `the edge is only there ${m[1]}¢ under the best bid`;
  if (/^pair: unpaired cap/.test(why)) return 'at the unpaired cap';
  if (/^pair: lock is too far behind the touch/.test(why)) return 'the lock is too far under the best bid';
  m = why.match(/^pair: bracket waits — the completion side cannot rest \((.+)\)$/);
  if (m) return `the bracket waits: the completion cannot rest (${notPostingText(m[1]) ?? plain(m[1])})`;
  m = why.match(/behind the touch \((\d+) under (\d+)/);
  if (m) return `behind the best bid (${m[1]}¢ under ${m[2]}¢)`;
  if (/^unwind: waits for a calm book/.test(why)) return 'the sell-back waits for a calm book';
  if (/^pull: nothing new while the line is off/.test(why)) return 'nothing new while Pinnacle is off';
  return null;
}

/** A reason with no translation, as decide wrote it, its rule prefix dropped (PR-063: never hide a blocker). */
function plain(why: string): string {
  return why.replace(/^(value|unwind|pull|pair|hold|follow|puppy|split|mini|harvest|stay): /, '');
}

/** Reasons that only restate the regime the sentence already names (a pull's policy, a hold's gate): not repeated per side. */
function restatesRegime(why: string, gate: string | null): boolean {
  return why === '' || (gate !== null && why === `hold: ${gate}`) || /^pull: (cancel all|cancel the adding side|hold|keep the reducing side|widened off the book)$/.test(why);
}

/** Why a side with nothing resting is not posting, in every regime (PR-063): a translation, else decide's own words. */
export function blockerText(why: string | null | undefined, gate: string | null): string | null {
  if (!why || restatesRegime(why, gate)) return null;
  return notPostingText(why) ?? plain(why);
}

/** Why a KEPT order is kept — the retained-order state (PR-063). Null for the plain "it stays" reasons. */
export function keptText(why: string | null | undefined): string | null {
  if (!why) return null;
  let m: RegExpMatchArray | null;
  if (/sell-back kept from the last line/.test(why)) return 'kept from the last line (nothing new while Pinnacle is off)';
  m = why.match(/^pair: completion sits behind its touch \((\d+) under (\d+); the lock allows (\d+)\)/);
  if (m) return `sits under the best bid ${m[2]}¢ (the lock allows ${m[3]}¢)`;
  if (/^pair: scratch \(hung\)/.test(why)) return 'scratching out of a hung pair (past the edge guard)';
  m = why.match(/^stay: pair \(last fair (\d+) s old, lock-guarded only\)/);
  if (m) return `kept against the lock (last line ${m[1]} s old)`;
  m = why.match(/^stay: value \(value: fair ([\d.]+) at or below the best bid (\d+)/);
  if (m) return `kept, but no new entry: Pinnacle fair ${m[1]}¢ is at or below the best bid ${m[2]}¢`;
  m = why.match(/^stay: puppy \((.+)\)$/);
  if (m) return `Puppy Love: ${m[1]}`;
  m = why.match(/^(?:stay|hold): split \((.+)\)$/);
  if (m) return `split: ${m[1]}`;
  m = why.match(/^mini: (orphan completion resting|attempt #\d+ resting) \((.+)\)$/);
  if (m) return `${m[1].replace(' resting', '')}: ${m[2]}`;
  return null;
}

/** The lines the richer snapshot adds while Pinnacle is off: the reference per side, why a side is not posting, and the
 *  tail she is selling back. Empty on an older relay. */
function offLineDetail(i: PenelopeStatusInput): string[] {
  const live = i.live;
  if (!live) return [];
  const out: string[] = [];
  const team = (s: 'A' | 'B') => (s === 'A' ? i.teamA : i.teamB);
  for (const s of ['A', 'B'] as const) {
    if (live.rests[s]) continue;
    const ref = live.ref?.[s];
    // A side about to place (its decision is a quote) is not blocked.
    const why = live.want?.[s]?.kind === 'quote' || live.auxWant?.[s]?.kind === 'quote' ? null : blockerText(live.desiredWhy?.[s], live.gate);
    if (ref && why) out.push(`${team(s)}: reference ${ref.px}¢ (${secs(ref.ageMs)} ago), not posting — ${why}`);
    else if (why) out.push(`${team(s)}: not posting — ${why}`);
    else if (ref) out.push(`${team(s)}: reference ${ref.px}¢ (${secs(ref.ageMs)} ago)`);
  }
  const t = live.tail;
  if (t && t.contracts > 0) out.push(`selling ${team(t.team)} back: ${t.contracts} unpaired at ${t.avgCents.toFixed(1)}¢`);
  return out;
}

/** What a resting order is FOR, in the trader's words (PEN-01: "is this opening, reducing or pairing?"). The relay
 *  sends the decision's reason with each rest; older relays send none and the rest reads as before. */
export function purposeOf(why: string | undefined, other: string, reducing = false): string {
  if (!why) return '';
  if (reducing && /^value/.test(why)) return ` (buying on Pinnacle value — it also sells ${other} back)`;
  if (/^unwind/.test(why)) return ` (selling ${other} back)`;
  if (/^split/.test(why)) return ' (split book: both best bids, value under the ceiling)';
  if (/scratch/.test(why)) return ' (scratching out of a hung pair)';
  if (/^pair: complete/.test(why)) return ' (completing the pair)';
  if (/^pair/.test(why)) return ' (pairing)';
  if (/^follow/.test(why)) return ' (buying, following the book up)';
  if (/^puppy/.test(why)) return ' (Puppy Love: buying into liquidity while Pinnacle is off)';
  if (/^mini: orphan completion/.test(why)) return ' (mini game: completing an orphan on Pinnacle value)';
  if (/^mini/.test(why)) return ' (mini game attempt)';
  if (/^value/.test(why)) return ' (buying)';
  return '';
}

/** One working order: team, price, what is left of what went in, what it is for, and — from the last decision — whether
 *  it is moving, coming off, or kept and why (PR-063). */
function restText(i: PenelopeStatusInput, s: Side, r: StatusRest, want: StatusWant | null | undefined, decided: string | null | undefined): string {
  const team = s === 'A' ? i.teamA : i.teamB;
  const other = s === 'A' ? i.teamB : i.teamA;
  const left = r.placed !== undefined && r.placed > r.size ? `×${r.size} of ${r.placed}` : `×${r.size}`;
  let t = `${team} ${r.px}¢ ${left}${purposeOf(r.why, other, i.live?.reducing?.[s] ?? false)}`;
  if (want?.kind === 'quote' && want.px !== null && want.px !== r.px) t += ` → moving to ${want.px}¢`;
  else if (want?.kind === 'none') {
    const why = blockerText(decided, i.live?.gate ?? null);
    t += why ? ` — coming off: ${why}` : ' — coming off';
  } else if (want?.kind === 'keep') {
    const kept = keptText(decided);
    if (kept) t += ` — ${kept}`;
  }
  return t;
}

function restsText(i: PenelopeStatusInput): string | null {
  const parts: string[] = [];
  const live = i.live;
  for (const s of ['A', 'B'] as const) {
    const main = live?.rests[s];
    if (main) parts.push(restText(i, s, main, live?.want?.[s], live?.desiredWhy?.[s]));
    const aux = live?.aux?.[s];
    if (aux) parts.push(restText(i, s, aux, live?.auxWant?.[s], live?.auxWhy?.[s]));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Why a side with nothing resting is not posting, outside PAIR (which has its own detail with the reference). */
function sideDetail(i: PenelopeStatusInput): string[] {
  const live = i.live;
  if (!live) return [];
  const out: string[] = [];
  for (const s of ['A', 'B'] as const) {
    if (live.rests[s] || live.aux?.[s] || live.want?.[s]?.kind === 'quote' || live.auxWant?.[s]?.kind === 'quote') continue;
    const why = blockerText(live.desiredWhy?.[s], live.gate);
    if (why) out.push(`${s === 'A' ? i.teamA : i.teamB}: not posting — ${why}`);
  }
  return out;
}

/** What held this pass's places back — pacing, or the venue's own checks — in the trader's words. */
export function pacingText(held: string): string {
  let m: RegExpMatchArray | null;
  if (held === 'trader limit backoff') return 'waiting out your limit refusal';
  m = held.match(/^place cap (\d+)\/min/);
  if (m) return `at the place cap (${m[1]} a minute)`;
  m = held.match(/^add cap (\d+)\/(\d+) contracts\/min/);
  if (m) return `at the add brake (${m[1]} of ${m[2]} contracts a minute)`;
  m = held.match(/^place outcome unknown on (.+)$/);
  if (m) return `waiting for Kalshi to confirm the last order on ${m[1]}`;
  return held;
}

function holdsText(i: PenelopeStatusInput): string[] {
  const live = i.live;
  if (!live) return [];
  const out: string[] = [];
  for (const s of ['A', 'B'] as const) {
    const v = live.venueHold?.[s];
    if (v) out.push(`${s === 'A' ? i.teamA : i.teamB}: the venue check held it — ${v}`);
  }
  if (live.heldBack) out.push(`held back: ${pacingText(live.heldBack.replace(/\b([AB])( \(aux\))?/g, (_, t: string, aux?: string) => `${t === 'A' ? i.teamA : i.teamB}${aux ?? ''}`))}`);
  return out;
}

/** Why a resting order is being held, from the gate text, in the trader's words. A depth dropout names the market and
 *  the numbers (Doom 10-17): "a depth dropout on the ORL market (19,876 of a 50,341 high, 39% left)". */
export function holdReason(gate: string | null, i: PenelopeStatusInput): string {
  if (!gate) return 'a thin book';
  if (/one-sided/.test(gate)) return 'a one-sided book';
  if (/stale/.test(gate)) return 'a quiet book';
  if (/spread/.test(gate)) return 'a wide spread';
  if (/depth/.test(gate)) {
    const m = /ticker ([ab]) \((\d+) vs (\d+)\)/.exec(gate);
    if (!m) return 'a depth dropout';
    const team = m[1] === 'a' ? i.teamA : i.teamB;
    const depth = Number(m[2]); const high = Number(m[3]);
    const pct = high > 0 ? Math.round((100 * depth) / high) : 0;
    return `a depth dropout on the ${team} market (${depth.toLocaleString('en-US')} of a ${high.toLocaleString('en-US')} high, ${pct}% left)`;
  }
  return 'a thin book';
}

const withDetail = (body: string, detail: string[]): string => (detail.length > 0 ? `${body} · ${detail.join(' · ')}` : body);

export function penelopeStatusSentence(i: PenelopeStatusInput): string {
  const suffix = i.practice ? ' (practice)' : '';
  if (!i.armed) return `stopped: not quoting${suffix}`;
  if (i.parked) return `not running: ${i.parked}`;
  const live = i.live;
  if (!live) return `starting…${suffix}`;
  const rests = restsText(i);
  let body: string;
  switch (live.regime) {
    case 'STOPPED':
      body = `stopped: ${live.stopReason ?? live.gate ?? 'game over'}`;
      break;
    case 'IDLE':
      body = `waiting: ${live.gate ?? 'game not started'}`;
      break;
    case 'PAUSED':
      body = `standing aside: ${live.gate ?? 'checking the book'}`;
      break;
    case 'HOLD':
      body = rests ? `holding ${rests} through ${holdReason(live.gate, i)}` : `nothing resting through ${holdReason(live.gate, i)}`;
      body = withDetail(body, sideDetail(i));
      break;
    case 'PULL':
      body = rests ? `holding ${rests} through a Pinnacle pull` : 'Pinnacle pulled its line: nothing resting';
      body = withDetail(body, sideDetail(i));
      break;
    case 'PAIR': {
      // 'stale' is not a pull: Pinnacle's price is simply older than max Pinnacle age, so it counts as off (WKU@SHSU 10-14,
      // where the board still showed the line and the sentence said "off").
      const quiet = live.fairBlock === 'stale' && live.vendorAgeMs != null;
      const head = quiet ? `Pinnacle quiet (its price is ${secs(live.vendorAgeMs!)} old, older than max Pinnacle age, so it counts as off)` : 'Pinnacle is off';
      const since = !quiet && live.lastFairAgeMs != null ? ` (last line ${secs(live.lastFairAgeMs)} ago)` : '';
      body = rests ? `${head}${since}: ${rests}` : `${head}${since}: nothing resting yet`;
      const detail = offLineDetail(i);
      if (detail.length > 0) body += ` · ${detail.join(' · ')}`;
      break;
    }
    case 'HARVEST':
      body = rests ? `harvesting: ${rests}` : 'harvesting: nothing resting (touches do not lock, or waiting for depth)';
      break;
    case 'MINI': {
      const w = live.desiredWhy?.A ?? live.desiredWhy?.B ?? null;
      const m = w ? w.match(/^mini: (.+)$/) : null;
      body = rests ? `mini game: ${rests}` : `mini game: nothing resting${m ? ` — ${m[1]}` : ''}`;
      break;
    }
    case 'VALUE':
    default:
      body = rests ? `resting ${rests}` : 'watching for edge: nothing resting';
      body = withDetail(body, sideDetail(i));
      if (live.fairStill) {
        const drift = live.fairStill.driftCents === null ? '' : `, consensus ${live.fairStill.driftCents.toFixed(1)}¢ away`;
        body += ` · Pinnacle unchanged for ${secs(live.fairStill.ageMs)} (still in the feed, game moving${drift}) — still used`;
      }
  }
  if (live.regime !== 'STOPPED' && live.regime !== 'IDLE' && live.regime !== 'PAUSED') body = withDetail(body, holdsText(i));
  if ((live.limitRejects ?? 0) > 0 && live.lastError && /cap|limit/i.test(live.lastError)) {
    body += ` · your limit refused the last add (${live.lastError})`;
  }
  return body + suffix;
}
